import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Logger } from "utils/Logger";
import { LocalStorageAuth } from "utils/LocalStorageAuth";
import { apiClient } from "utils/ApiClient";
import { API_LOGIN_URL, API_RESTORE_SESSION_URL, API_BASE_URL, COOKIE_TOKEN_ID } from 'envConfig';
import { Company, User, CompanyMembership } from "modules/Interfaces/Company";
import { CookieUtils } from "utils/CookieUtils";

interface AuthenticationState {
    loading: boolean;
    user: User | null;
    company: Company | null;
    activeCompanyId: string | null;
    companiesList: CompanyMembership[];
    error: any;
    registering: boolean;
    registerError: string | null;
    registerSuccess: boolean;
};

export interface RegisterPayload {
    email: string;
    password?: string;
    mobile: string;
    companyName?: string;
    businessPlan?: string;
    businessPlanId?: string;
    companyAddressLine1?: string;
    companyAddressLine2?: string;
    companyCity?: string;
    companyCountry?: string;
    companyZip?: string;
    companyWebsite?: string;
    companyPhone?: string;
    profile: {
        firstName: string;
        lastName: string;
        country: string;
        termsAndConditions: boolean;
        privacyPolicy: boolean;
        emailNotifications: boolean;
        smsNotifications: boolean;
        whatsAppNotifications?: boolean;
        dob: string;
    };
    module?: string;
}

export interface ModuleLicenseInfo {
    module: string;
    maxUsers: number;
    registeredUsers: number;
    remainingSeats: number;
}

export interface CompanyPreview {
    _id: string;
    name: string;
    addressLine1?: string;
    city: string;
    country: string;
    companyEmail?: string;
    website?: string;
    logoLight?: string | null;
    logoDark?: string | null;
    availableModules?: string[];
    moduleLicenseInfo?: ModuleLicenseInfo[];
    registeredUserCount?: number;
    maxUsersAllowed?: number;
}

export interface JoinCompanyPayload {
    email: string;
    password: string;
    mobile: string;
    companyId: string;
    moduleAndRole: { module: string; role: string }[];
    profile: {
        firstName: string;
        lastName: string;
        country: string;
        termsAndConditions: boolean;
        privacyPolicy: boolean;
        emailNotifications: boolean;
        smsNotifications: boolean;
        whatsAppNotifications?: boolean;
        dob: string;
    };
}

export interface CheckoutSessionPayload {
    email: string;
    password?: string;
    mobile: string;
    companyName?: string;
    businessPlan?: string;
    businessPlanId?: string;
    billingCycle: 'monthly' | 'annual';
    currency?: string;
    companyAddressLine1?: string;
    companyAddressLine2?: string;
    companyCity?: string;
    companyCountry?: string;
    companyZip?: string;
    companyWebsite?: string;
    companyPhone?: string;
    profile: {
        firstName: string;
        lastName: string;
        country: string;
        termsAndConditions: boolean;
        privacyPolicy: boolean;
        emailNotifications: boolean;
        smsNotifications: boolean;
        whatsAppNotifications?: boolean;
        dob: string;
    };
}

function normalizeEmailInput(email: string): string {
    return email.trim().toLowerCase();
}

const initialState : AuthenticationState = {
    loading: false,
    user: null,
    company: null,
    activeCompanyId: LocalStorageAuth.getActiveCompanyId(),
    companiesList: [],
    error: null,
    registering: false,
    registerError: null,
    registerSuccess: false,
};

export const loginUserAsync = createAsyncThunk(
    "authentication/loginUser",
    async (credentials: { email: string, password: string }) => {
        Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `loginUserAsync(): credentials = ${JSON.stringify(credentials)}`);
        
        // Use ApiClient for the request (automatically handles headers and errors)
        const data = await apiClient.post('/auth/login', {
            ...credentials,
            email: normalizeEmailInput(credentials.email),
        });
        Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `loginUserAsync(): data = ${JSON.stringify(data)}`);
        
        return data;
    }
);

export const registerUserAsync = createAsyncThunk(
    "authentication/registerUser",
    async (payload: RegisterPayload, { rejectWithValue }) => {
        try {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `registerUserAsync(): payload = ${JSON.stringify(payload)}`);
            const data = await apiClient.post('/auth/register', { ...payload, email: normalizeEmailInput(payload.email) });
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `registerUserAsync(): data = ${JSON.stringify(data)}`);
            if (data.status && data.status >= 400) {
                return rejectWithValue(data.message || 'Registration failed');
            }
            return data;
        } catch (error: any) {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `registerUserAsync(): error = ${error}`);
            return rejectWithValue(error.message || 'Registration failed. Please try again.');
        }
    }
);

export const fetchCountriesAsync = createAsyncThunk(
    "authentication/fetchCountries",
    async (_, { rejectWithValue }) => {
        try {
            const data = await apiClient.get('/auth/countries');
            if (data.status && data.status >= 400) {
                return rejectWithValue(data.message || 'Failed to load countries');
            }
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to load countries');
        }
    }
);

export const fetchBusinessPlansAsync = createAsyncThunk(
    "authentication/fetchBusinessPlans",
    async (_, { rejectWithValue }) => {
        try {
            const data = await apiClient.get('/auth/business-plans');
            if (data.status && data.status >= 400) {
                return rejectWithValue(data.message || 'Failed to load plans');
            }
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to load business plans');
        }
    }
);

export const fetchSignupPlansAsync = createAsyncThunk(
    "authentication/fetchSignupPlans",
    async (params: { country: string; rootDomain: string }, { rejectWithValue }) => {
        try {
            const data = await apiClient.get(`/auth/signup-plans?country=${encodeURIComponent(params.country)}&rootDomain=${encodeURIComponent(params.rootDomain)}`);
            if (data.status && data.status >= 400) {
                return rejectWithValue(data.message || 'Failed to load plans');
            }
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to load business plans');
        }
    }
);

export const fetchCompanyPreviewAsync = createAsyncThunk(
    "authentication/fetchCompanyPreview",
    async (companyId: string, { rejectWithValue }) => {
        try {
            Logger.debug("authenticationSlice.ts", `fetchCompanyPreviewAsync(): companyId = ${companyId}`);
            const data = await apiClient.get(`/auth/company-preview/${companyId}`);
            Logger.debug("authenticationSlice.ts", `fetchCompanyPreviewAsync(): data = ${JSON.stringify(data)}`);
            if (data.status && data.status >= 400) {
                return rejectWithValue(data.message || 'Company not found');
            }
            return data;
        } catch (error: any) {
            Logger.debug("authenticationSlice.ts", `fetchCompanyPreviewAsync(): error = ${error}`);
            return rejectWithValue(error.message || 'Failed to fetch company details');
        }
    }
);

export const registerUserToCompanyAsync = createAsyncThunk(
    "authentication/registerUserToCompany",
    async (payload: JoinCompanyPayload, { rejectWithValue }) => {
        try {
            const data = await apiClient.post('/auth/register-user', { ...payload, email: normalizeEmailInput(payload.email) });
            if (data.status && data.status >= 400) {
                return rejectWithValue(data.message || 'Registration failed');
            }
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Registration failed. Please try again.');
        }
    }
);

export const createCheckoutSessionAsync = createAsyncThunk(
    "authentication/createCheckoutSession",
    async (payload: CheckoutSessionPayload, { rejectWithValue }) => {
        try {
            const data = await apiClient.post('/auth/create-checkout-session', { ...payload, email: normalizeEmailInput(payload.email) });
            if (data.status && data.status >= 400) {
                return rejectWithValue(data.message || 'Failed to create checkout session');
            }
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to initiate payment. Please try again.');
        }
    }
);

export const verifyPaymentAndRegisterAsync = createAsyncThunk(
    "authentication/verifyPaymentAndRegister",
    async (sessionId: string, { rejectWithValue }) => {
        try {
            const data = await apiClient.post('/auth/verify-payment-and-register', { sessionId });
            if (data.status && data.status >= 400) {
                return rejectWithValue(data.message || 'Payment verification failed');
            }
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Payment verification failed. Please contact support.');
        }
    }
);

export const activateAccountAsync = createAsyncThunk(
    "authentication/activateAccount",
    async ({ id, token }: { id: string; token: string }, { rejectWithValue }) => {
        try {
            const data = await apiClient.get(`/auth/activate?id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}`);
            if (data.status && data.status >= 400) {
                return rejectWithValue(data.message || 'Activation failed');
            }
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Account activation failed.');
        }
    }
);

export const restoreReduxSession = createAsyncThunk(
    "authentication/restoreSession",
    async (_, { rejectWithValue }) => {
        try {
            const token = LocalStorageAuth.getToken();
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `restoreSession(): token = ${token}`);
            
            if (!token) {
                Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", "restoreSession(): No token found in local storage");
                throw new Error("No authentication token found");
            }
            
            // Use ApiClient instead of manual fetch (automatically includes Authorization header)
            // Use just the endpoint path since ApiClient will prepend the base URL
            const endpoint = `/auth/restoresession/${token}`;
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `restoreSession(): Calling API endpoint: ${endpoint}`);
            const data = await apiClient.get(endpoint);
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `restoreSession(): data = ${JSON.stringify(data)}`);
            
            return data;
        } catch (error) {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `restoreSession(): error = ${error}`);
            return rejectWithValue((error as Error).message || 'Failed to restore session');
        }
    }
);

export const fetchCompanyDetailsAsync = createAsyncThunk(
    "authentication/fetchCompanyDetails",
    async (companyId: string) => {
        Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCompanyDetailsAsync(): companyId = ${companyId}`);
        // Use ApiClient instead of manual fetch (automatically includes Authorization header)
        const data = await apiClient.get(`/company/${companyId}`);
        Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCompanyDetailsAsync(): data = ${JSON.stringify(data)}`);
        return data;
    }
);

export const checkEmailExistsAsync = createAsyncThunk(
    "authentication/checkEmailExists",
    async (email: string, { rejectWithValue }) => {
        try {
            const data = await apiClient.post('/auth/check-email', { email: normalizeEmailInput(email) });
            if (data.status && data.status >= 400) {
                return rejectWithValue(data.message || 'Failed to check email');
            }
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to check email');
        }
    }
);

export const switchCompanyAsync = createAsyncThunk(
    "authentication/switchCompany",
    async (companyId: string) => {
        LocalStorageAuth.setActiveCompanyId(companyId);
        const data = await apiClient.get(`/company/${companyId}`);
        return { companyId, companyData: data };
    }
);

export const fetchCurrentUserDetailsAsync = createAsyncThunk(
    "authentication/fetchCurrentUserDetails",
    async (userId: string) => {
        Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCurrentUserDetailsAsync(): userId = ${userId}`);
        // Use ApiClient instead of manual fetch (automatically includes Authorization header)
        const data = await apiClient.get(`/user/${userId}`);
        Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCurrentUserDetailsAsync(): data = ${JSON.stringify(data)}`);
        return data;
    }
);

const authenticationSlice = createSlice({
    name: "authentication",
    initialState,
    extraReducers: (builder) => {
        builder.addCase(loginUserAsync.pending, (state) => {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `loginUserAsync.pending : state = ${JSON.stringify(state)}`);
            state.loading = true;
            state.error = null;
            // Don't clear user/company on pending to prevent flickering during login
        });
        builder.addCase(loginUserAsync.fulfilled, (state, action) => {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `loginUserAsync.fulfilled : state = ${JSON.stringify(state)}`);
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `loginUserAsync.fulfilled : action = ${JSON.stringify(action)}`);
            
            // CRITICAL DEBUGGING: Log the entire response structure to find the token
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", "loginUserAsync.fulfilled: Full action.payload structure:");
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `action.payload: ${JSON.stringify(action.payload, null, 2)}`);
            
            // Check different possible token locations
            let sessionToken = null;
            
            if (action.payload && action.payload.data) {
                Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", "loginUserAsync.fulfilled: Checking action.payload.data for token");
                
                // Try different possible token field names
                if (action.payload.data.token) {
                    sessionToken = action.payload.data.token;
                    Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", "loginUserAsync.fulfilled: Found token in action.payload.data.token");
                } else if (action.payload.data.sessionToken) {
                    sessionToken = action.payload.data.sessionToken;
                    Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", "loginUserAsync.fulfilled: Found token in action.payload.data.sessionToken");
                } else if (action.payload.data.authentication && action.payload.data.authentication.sessionToken) {
                    sessionToken = action.payload.data.authentication.sessionToken;
                    Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", "loginUserAsync.fulfilled: Found token in action.payload.data.authentication.sessionToken");
                } else if (action.payload.data.accessToken) {
                    sessionToken = action.payload.data.accessToken;
                    Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", "loginUserAsync.fulfilled: Found token in action.payload.data.accessToken");
                } else {
                    Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", "loginUserAsync.fulfilled: ⚠️ No token found in action.payload.data");
                    Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `action.payload.data keys: ${Object.keys(action.payload.data)}`);
                }
            } else {
                Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", "loginUserAsync.fulfilled: ⚠️ action.payload.data is missing or undefined");
            }
            
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `loginUserAsync.fulfilled : Final sessionToken value: ${sessionToken}`);
            
            if (sessionToken) {
                state.loading = false;
                state.user = action.payload.data;
                state.company = null;
                state.error = null;

                const companiesList: CompanyMembership[] = action.payload.data?.companiesList || [];
                state.companiesList = companiesList;

                const storedActiveId = LocalStorageAuth.getActiveCompanyId();
                const validStored = storedActiveId && companiesList.some((c: CompanyMembership) => c.companyId === storedActiveId);
                if (validStored) {
                    state.activeCompanyId = storedActiveId;
                } else {
                    const preferred = companiesList.find((c: CompanyMembership) => c.hasAccess && c.isMaster)
                        || companiesList.find((c: CompanyMembership) => c.hasAccess)
                        || companiesList[0];
                    const newActiveId = preferred?.companyId || action.payload.data?.companyId || null;
                    state.activeCompanyId = newActiveId;
                    if (newActiveId) LocalStorageAuth.setActiveCompanyId(newActiveId);
                }
                
                LocalStorageAuth.setToken(sessionToken);
                LocalStorageAuth.setUser(action.payload.data);
                
                Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `loginUserAsync.fulfilled: activeCompanyId=${state.activeCompanyId}, companiesList count=${companiesList.length}`);
            } else {
                Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", "loginUserAsync.fulfilled: ❌ No valid token found, cannot set cookies");
                state.loading = false;
                state.error = "No authentication token received from server";
                state.user = null;
                state.company = null;
            }
        });
        builder.addCase(loginUserAsync.rejected, (state, action) => {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `loginUserAsync.rejected : state = ${JSON.stringify(state)}`);
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `loginUserAsync.rejected : action = ${JSON.stringify(action)}`);
            state.loading = false;
            state.error = action.error.message;
            state.user = null;
            state.company = null;
            // Clear local storage on login failure
            LocalStorageAuth.clearAll();
        });
        builder.addCase(restoreReduxSession.pending, (state) => {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `restoreReduxSession.pending : state = ${JSON.stringify(state)}`);
            state.loading = true;
            state.error = null;
            state.user = null;
            state.company = null;
        });
        builder.addCase(restoreReduxSession.fulfilled, (state, action) => {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `restoreReduxSession.fulfilled : action status = ${action.payload?.status}`);
            state.loading = false;
            if (action.payload.status === 200) {
                state.user = action.payload.data;
                state.error = null;

                const companiesList: CompanyMembership[] = action.payload.data?.companiesList || [];
                state.companiesList = companiesList;

                const storedActiveId = LocalStorageAuth.getActiveCompanyId();
                const validStored = storedActiveId && companiesList.some((c: CompanyMembership) => c.companyId === storedActiveId);
                if (validStored) {
                    state.activeCompanyId = storedActiveId;
                } else {
                    const preferred = companiesList.find((c: CompanyMembership) => c.hasAccess && c.isMaster)
                        || companiesList.find((c: CompanyMembership) => c.hasAccess)
                        || companiesList[0];
                    const newActiveId = preferred?.companyId || action.payload.data?.companyId || null;
                    state.activeCompanyId = newActiveId;
                    if (newActiveId) LocalStorageAuth.setActiveCompanyId(newActiveId);
                }

                Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `restoreReduxSession.fulfilled : activeCompanyId=${state.activeCompanyId}, companiesList count=${companiesList.length}`);
            } else {
                state.user = null;
                state.company = null;
                state.activeCompanyId = null;
                state.companiesList = [];
                state.error = action.payload.message;
                LocalStorageAuth.clearAll();
            }
        });
        builder.addCase(restoreReduxSession.rejected, (state, action) => {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `restoreReduxSession.rejected : state = ${JSON.stringify(state)}`);
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `restoreReduxSession.rejected : action = ${JSON.stringify(action)}`);
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `restoreReduxSession.rejected : Current cookies before deletion: ${document.cookie}`);
            
            state.loading = false;
            state.error = action.error.message;
            state.user = null;
            state.company = null;
            
            // Only delete cookies if we have a specific error that warrants it
            // Don't delete cookies on network errors or temporary failures
            if (action.error.message && (
                action.error.message.includes('Failed to fetch') ||
                action.error.message.includes('Network Error') ||
                action.error.message.includes('timeout')
            )) {
                Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", "restoreReduxSession.rejected : Network error detected, preserving cookies for retry");
            } else {
                Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `restoreReduxSession.rejected : DELETING COOKIE = tokenid = ${CookieUtils.getCookie(COOKIE_TOKEN_ID)}`);
                CookieUtils.deleteCookie(COOKIE_TOKEN_ID);
                // Also delete from cross-domain
                CookieUtils.deleteCookie(COOKIE_TOKEN_ID, '.campaignking.ai');
            }
        });
        
        // Register user async thunk cases
        builder.addCase(registerUserAsync.pending, (state) => {
            state.registering = true;
            state.registerError = null;
            state.registerSuccess = false;
        });
        builder.addCase(registerUserAsync.fulfilled, (state) => {
            state.registering = false;
            state.registerError = null;
            state.registerSuccess = true;
        });
        builder.addCase(registerUserAsync.rejected, (state, action) => {
            state.registering = false;
            state.registerError = (action.payload as string) || action.error.message || 'Registration failed';
            state.registerSuccess = false;
        });

        builder.addCase(registerUserToCompanyAsync.pending, (state) => {
            state.registering = true;
            state.registerError = null;
            state.registerSuccess = false;
        });
        builder.addCase(registerUserToCompanyAsync.fulfilled, (state) => {
            state.registering = false;
            state.registerError = null;
            state.registerSuccess = true;
        });
        builder.addCase(registerUserToCompanyAsync.rejected, (state, action) => {
            state.registering = false;
            state.registerError = (action.payload as string) || action.error.message || 'Registration failed';
            state.registerSuccess = false;
        });

        // Fetch company details async thunk cases
        builder.addCase(fetchCompanyDetailsAsync.pending, (state) => {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCompanyDetailsAsync.pending : state = ${JSON.stringify(state)}`);
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchCompanyDetailsAsync.fulfilled, (state, action) => {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCompanyDetailsAsync.fulfilled : state = ${JSON.stringify(state)}`);
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCompanyDetailsAsync.fulfilled : action = ${JSON.stringify(action)}`);
            state.loading = false;
            // The company data is wrapped in action.payload.data
            if (action.payload && action.payload.data && action.payload.data._id) {
                state.company = action.payload.data;
                state.error = null;
                Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCompanyDetailsAsync.fulfilled : company data set = ${JSON.stringify(action.payload.data)}`);
            } else {
                state.company = null;
                state.error = "Invalid company data received";
                Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCompanyDetailsAsync.fulfilled : invalid company data = ${JSON.stringify(action.payload)}`);
            }
        });
        builder.addCase(fetchCompanyDetailsAsync.rejected, (state, action) => {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCompanyDetailsAsync.rejected : state = ${JSON.stringify(state)}`);
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCompanyDetailsAsync.rejected : action = ${JSON.stringify(action)}`);
            state.loading = false;
            state.error = action.error.message;
            state.company = null;
        });
        
        // Fetch current user details async thunk cases
        builder.addCase(fetchCurrentUserDetailsAsync.pending, (state) => {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCurrentUserDetailsAsync.pending : state = ${JSON.stringify(state)}`);
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchCurrentUserDetailsAsync.fulfilled, (state, action) => {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCurrentUserDetailsAsync.fulfilled : state = ${JSON.stringify(state)}`);
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCurrentUserDetailsAsync.fulfilled : action = ${JSON.stringify(action)}`);
            state.loading = false;
            if (action.payload && action.payload.data) {
                state.user = action.payload.data;
                state.error = null;
                Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCurrentUserDetailsAsync.fulfilled : user data updated = ${JSON.stringify(action.payload.data)}`);
            } else {
                state.error = "Invalid user data received";
                Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCurrentUserDetailsAsync.fulfilled : invalid user data = ${JSON.stringify(action.payload)}`);
            }
        });
        builder.addCase(fetchCurrentUserDetailsAsync.rejected, (state, action) => {
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCurrentUserDetailsAsync.rejected : state = ${JSON.stringify(state)}`);
            Logger.debug("st-ck-client/src/state/authentication/authenticationSlice.ts", `fetchCurrentUserDetailsAsync.rejected : action = ${JSON.stringify(action)}`);
            state.loading = false;
            state.error = action.error.message;
        });
        builder.addCase(switchCompanyAsync.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(switchCompanyAsync.fulfilled, (state, action) => {
            state.loading = false;
            state.activeCompanyId = action.payload.companyId;
            if (action.payload.companyData?.data?._id) {
                state.company = action.payload.companyData.data;
            }
            const targetMembership = state.companiesList.find(
                (c) => c.companyId === action.payload.companyId
            );
            if (targetMembership && state.user) {
                state.user.moduleAndRole = targetMembership.moduleAndRole;
            }
        });
        builder.addCase(switchCompanyAsync.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });
    },
    reducers: {
        logout: (state) => {
            state.user = null;
            state.company = null;
            state.activeCompanyId = null;
            state.companiesList = [];
            state.loading = false;
            state.error = null;
            LocalStorageAuth.clearAll();
        },
        switchActiveCompany: (state, action) => {
            state.activeCompanyId = action.payload;
            state.company = null;
            LocalStorageAuth.setActiveCompanyId(action.payload);
        },
        clearCompanyData: (state) => {
            state.company = null;
        },
        updateCompanyData: (state, action) => {
            state.company = action.payload;
        },
        updateUserModuleAndRole: (state, action) => {
            if (state.user) {
                state.user.moduleAndRole = action.payload;
            }
        },
        updateUserData: (state, action) => {
            state.user = action.payload;
        },
        clearRegistration: (state) => {
            state.registering = false;
            state.registerError = null;
            state.registerSuccess = false;
        }
    }
});

export const { clearCompanyData, updateCompanyData, updateUserModuleAndRole, updateUserData, logout, clearRegistration, switchActiveCompany } = authenticationSlice.actions;
export default authenticationSlice.reducer;
